import React, { useState, useEffect } from 'react';
import { utentiAPI, mediciAPI } from '../services/api';
import { useRuoli, etichettaRuolo as etichettaRuoloDa } from '../utils/ruoli';
import { useAuthStore } from '../store/useStore';
import { UserCog } from 'lucide-react';
import IntestazioneSezione from '../components/comuni/IntestazioneSezione';
import { useSporco, useUscitaProtetta } from '../utils/uscitaProtetta';
import '../styles/GestioneUtenti.css';

/**
 * GestioneUtenti — account del gestionale.
 *
 * CHI CI ARRIVA
 * Admin e segreteria. La segreteria accoglie una collega nuova il primo
 * giorno e deve poterle aprire l'accesso senza aspettare l'admin.
 *
 * COSA CAMBIA PER LA SEGRETERIA
 * Crea utenti, ma NON amministratori, e non tocca password ne' stato di
 * nessuno. Il divieto vero e' sulle rotte - `POST /api/utenti` rifiuta il
 * ruolo admin a chi non e' admin, password e stato sono solo admin - e
 * quello che si nasconde qui serve solo a non mostrare bottoni che
 * darebbero un errore.
 */

/* I ruoli NON sono scritti qui: arrivano da /api/ruoli (config/ruoli.js
   nel backend, verificato all'avvio contro il vincolo del database).
   Fino al 21 settembre 2026 c'era un elenco a mano, e `medico_senior`
   ci mancava. Vedi utils/ruoli.js.
   `RUOLI_CLINICI` non e' un elenco di ruoli: dice quali ruoli vogliono
   un medico collegato, che e' una regola del modulo, non un'anagrafica. */
const RUOLI_CLINICI = ['medico', 'medico_senior'];

/**
 * L'interceptor in services/api.js rigetta gia' il BODY della risposta
 * (error.response.data), non l'oggetto errore di axios: il messaggio del
 * backend si trova quindi in e.message. Il ramo e.response.data.message
 * resta come rete di sicurezza per errori che non passano dall'interceptor.
 */
const messaggioErrore = (e, ripiego) =>
    e?.message || e?.response?.data?.message || ripiego;

const FORM_VUOTO = {
    username: '', nome: '', cognome: '', telefono: '',
    email: '', ruolo: 'segreteria', medico_id: ''
};

const GestioneUtenti = () => {
    const user = useAuthStore((state) => state.user);
    const isAdmin = user?.ruolo === 'admin';

    /* Due elenchi, due scopi, tenuti separati di proposito: `ruoli` e'
       tutto cio' che esiste e serve ai filtri; `assegnabili` e' cio' che
       chi e' collegato puo' creare, deciso dal server. Un'opzione che la
       rotta rifiuterebbe non si mostra: un errore dopo aver compilato
       sette campi e' peggio di un'opzione che non c'e'. */
    const { ruoli: RUOLI, assegnabili } = useRuoli();
    const ruoliCreabili = RUOLI.filter((r) => assegnabili.includes(r.valore));
    const etichettaRuolo = (r) => etichettaRuoloDa(RUOLI, r);

    const [utenti, setUtenti] = useState([]);
    const [medici, setMedici] = useState([]);
    const [caricamento, setCaricamento] = useState(true);
    const [mostraForm, setMostraForm] = useState(false);
    const [form, setForm] = useState(FORM_VUOTO);
    /* Il modulo dell'utente nuovo chiede conferma se si chiude con
       qualcosa di scritto: sfondo e «Annulla» passano da chiudiForm().
       Il modulo resta FORM_VUOTO quando e' chiuso, quindi il confronto e'
       con il vuoto. */
    const [versioneForm, setVersioneForm] = useState(0);
    const sporco = useSporco(mostraForm ? form : null, versioneForm);
    const chiedi = useUscitaProtetta(sporco);
    const apriForm = () => { setMostraForm(true); setErrore(null); setVersioneForm((v) => v + 1); };
    const chiudiForm = () => { if (chiedi()) { setMostraForm(false); setForm(FORM_VUOTO); } };
    const [salvataggio, setSalvataggio] = useState(false);
    const [errore, setErrore] = useState(null);

    // Reset password
    const [resetPer, setResetPer] = useState(null); // utente selezionato
    const [azzeraPer, setAzzeraPer] = useState(null); // azzeramento del secondo fattore
    const [azzerando, setAzzerando] = useState(false);
    const [esitoAzzera, setEsitoAzzera] = useState('');
    const [reinviaPer, setReinviaPer] = useState(null);   // reinvio delle credenziali
    const [reinviando, setReinviando] = useState(false);
    const [esitoReinvio, setEsitoReinvio] = useState('');
    const [nuovaPassword, setNuovaPassword] = useState('');

    // Password appena generata da mostrare all'admin una volta sola
    const [credenziali, setCredenziali] = useState(null);

    // Filtri: '' vale per tutti
    const [filtroRuolo, setFiltroRuolo] = useState('');
    const [ricerca, setRicerca] = useState('');

    const caricaUtenti = async () => {
        try {
            setCaricamento(true);
            const r = await utentiAPI.getAll();
            setUtenti(r.data.data.utenti || []);
        } catch (e) {
            console.error('Errore caricamento utenti:', e);
            setErrore(messaggioErrore(e, 'Impossibile caricare gli utenti'));
        } finally {
            setCaricamento(false);
        }
    };

    useEffect(() => {
        caricaUtenti();
        mediciAPI.getAll(true)
            .then((r) => setMedici(r.data?.data?.medici || []))
            .catch(() => {});
    }, []);

    // Medici clinici non ancora collegati ad alcun account
    const mediciDisponibili = medici.filter(
        (m) => !utenti.some((u) => String(u.medico_id) === String(m.id))
    );

    const aggiorna = (campo, valore) => {
        setForm((prec) => {
            const nuovo = { ...prec, [campo]: valore };
            // Se il ruolo non è clinico, azzera il medico collegato
            if (campo === 'ruolo' && !RUOLI_CLINICI.includes(valore)) {
                nuovo.medico_id = '';
            }
            return nuovo;
        });
    };

    const salva = async () => {
        setErrore(null);
        try {
            setSalvataggio(true);
            const r = await utentiAPI.create({
                ...form,
                medico_id: form.medico_id || null
            });
            const creato = r.data.data;
            setForm(FORM_VUOTO);
            setMostraForm(false);
            caricaUtenti();
            setCredenziali({
                nome: `${creato.utente.cognome} ${creato.utente.nome}`,
                username: creato.utente.username,
                emailInviata: creato.email_inviata === true,
                emailDestinatario: creato.email_destinatario || creato.utente.email,
            });
        } catch (e) {
            console.error('Errore creazione utente:', e);
            setErrore(messaggioErrore(e, 'Errore nella creazione'));
        } finally {
            setSalvataggio(false);
        }
    };

    const commutaStato = async (u) => {
        try {
            await utentiAPI.setStato(u.id, !u.attivo);
            caricaUtenti();
        } catch (e) {
            setErrore(messaggioErrore(e, 'Errore nel cambio di stato'));
        }
    };

    const confermaReset = async () => {
        if (!nuovaPassword || nuovaPassword.length < 8) {
            setErrore('La password deve avere almeno 8 caratteri');
            return;
        }
        try {
            await utentiAPI.resetPassword(resetPer.id, nuovaPassword);
            setResetPer(null);
            setNuovaPassword('');
            setErrore(null);
        } catch (e) {
            setErrore(messaggioErrore(e, 'Errore nel reset'));
        }
    };

    /* Reinviare le credenziali vuol dire GENERARE una password nuova: la
       precedente non vale piu' dal momento in cui l'email parte. E' un
       reset, con la differenza che la password non passa da chi preme.
       Se l'email non parte il server non cambia niente e lo dice. */
    const confermaReinvio = async () => {
        setReinviando(true);
        try {
            const r = await utentiAPI.reinviaCredenziali(reinviaPer.id);
            setEsitoReinvio(r.data.message);
            setReinviaPer(null);
            setErrore(null);
            caricaUtenti();
        } catch (e) {
            setErrore(messaggioErrore(e, 'Errore nel reinvio'));
        } finally {
            setReinviando(false);
        }
    };

    /* L'azzeramento del secondo fattore e' l'operazione piu' delicata di
       questa schermata: toglie a una persona la protezione che la
       difende, e lo fa un altro. La finestra di conferma dice per esteso
       che cosa comporta e che resta scritto - e' rumore voluto, per
       questo esiste anche l'avviso ad alta priorita' dall'altra parte. */
    const confermaAzzera = async () => {
        setAzzerando(true);
        try {
            const r = await utentiAPI.azzera2fa(azzeraPer.id);
            setEsitoAzzera(r.data.message);
            setAzzeraPer(null);
            setErrore(null);
            caricaUtenti();
        } catch (e) {
            setErrore(messaggioErrore(e, 'Errore nell’azzeramento'));
        } finally {
            setAzzerando(false);
        }
    };

    /* La ricerca guarda nome, cognome, utente ed email: si cerca
       con quello che si ha in mente, non con un campo prestabilito. */
    const utentiVisibili = utenti.filter((u) => {
        if (filtroRuolo && u.ruolo !== filtroRuolo) return false;
        const q = ricerca.trim().toLowerCase();
        if (!q) return true;
        return [u.nome, u.cognome, u.username, u.email]
            .some((v) => String(v || '').toLowerCase().includes(q));
    });

    return (
        <div className="gu-wrap">
            <IntestazioneSezione
                titolo="Gestione utenti"
                icona={<UserCog size={22} aria-hidden="true" />}
                azione={
                    <button className="gu-btn-primario" onClick={apriForm}>
                        + Nuovo utente
                    </button>
                }
            />

            <div className="gu-filtri" data-non-trattiene>
                <input
                    className="gu-ricerca"
                    value={ricerca}
                    onChange={(e) => setRicerca(e.target.value)}
                    placeholder="Cerca per nome, utente o email…"
                />
                <div className="gu-ruoli">
                    <button className={filtroRuolo === '' ? 'attivo' : ''}
                            onClick={() => setFiltroRuolo('')}>
                        Tutti
                    </button>
                    {RUOLI.map((r) => (
                        <button key={r.valore}
                                className={filtroRuolo === r.valore ? 'attivo' : ''}
                                onClick={() => setFiltroRuolo(r.valore)}>
                            {r.etichetta}
                            <span className="gu-conteggio">
                                {utenti.filter((u) => u.ruolo === r.valore).length}
                            </span>
                        </button>
                    ))}
                </div>
            </div>

            {errore && !mostraForm && !resetPer && <div className="gu-errore">{errore}</div>}

            {caricamento ? (
                <div className="gu-stato">Caricamento…</div>
            ) : (
                utentiVisibili.length === 0 ? (
                    <div className="gu-stato">
                        Nessun utente corrisponde ai filtri.
                        <button className="gu-azzera" onClick={() => { setRicerca(''); setFiltroRuolo(''); }}>
                            Mostra tutti
                        </button>
                    </div>
                ) : (
                <div className="rm-scorre">
                <table className="gu-tabella">
                    <thead>
                        <tr>
                            <th>Nome</th>
                            <th>Username</th>
                            <th>Email</th>
                            <th>Telefono</th>
                            <th>Ruolo</th>
                            <th>Medico</th>
                            <th>Ultimo accesso</th>
                            <th>Due passaggi</th>
                            <th>Stato</th>
                            <th>Azioni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {utentiVisibili.map((u) => (
                            <tr key={u.id} className={u.attivo ? '' : 'gu-disattivo'}>
                                <td>{u.cognome} {u.nome}</td>
                                <td>{u.username}</td>
                                <td className="gu-email" title={u.email || ''}><span className="rm-tronca">{u.email}</span></td>
                                <td>{u.telefono || '—'}</td>
                                <td><span className={`gu-ruolo gu-ruolo-${u.ruolo}`}>{etichettaRuolo(u.ruolo)}</span></td>
                                <td>{u.medico_cognome ? `Dr. ${u.medico_cognome}` : '—'}</td>
                                <td className="rm-intero">{u.ultimo_accesso ? new Date(u.ultimo_accesso).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : 'mai'}</td>
                                <td>
                                    {u.totp_stato === 'attivo' ? (
                                        <span className="gu-2fa-si"
                                              title={u.totp_attivato_il
                                                  ? `Attiva dal ${new Date(u.totp_attivato_il).toLocaleDateString('it-IT')}`
                                                  : 'Attiva'}>
                                            Attiva
                                            {u.totp_attivato_il && (
                                                <> · <span className="rm-intero">dal {new Date(u.totp_attivato_il).toLocaleDateString('it-IT')}</span></>
                                            )}
                                        </span>
                                    ) : (
                                        /* «in attesa» vuol dire che ha cominciato
                                           l'attivazione e non l'ha confermata: per
                                           entrare non gli serve, quindi qui conta
                                           come non attiva e si dice perche'. */
                                        <span className="gu-2fa-no">
                                            {u.totp_stato === 'in_attesa' ? 'iniziata, non confermata' : 'no'}
                                        </span>
                                    )}
                                </td>
                                <td>
                                    <span className={`gu-stato-badge ${u.attivo ? 'attivo' : 'sospeso'}`}>
                                        {u.attivo ? 'Attivo' : 'Sospeso'}
                                    </span>
                                </td>
                                <td>
                                    {isAdmin ? (
                                        <div className="gu-azioni">
                                            <button className="gu-link" onClick={() => { setResetPer(u); setNuovaPassword(''); setErrore(null); }}>
                                                Password
                                            </button>
                                            {u.attivo && (
                                                <button className="gu-link"
                                                        onClick={() => { setReinviaPer(u); setErrore(null); }}>
                                                    Invia credenziali
                                                </button>
                                            )}
                                            <button className="gu-link" onClick={() => commutaStato(u)}>
                                                {u.attivo ? 'Sospendi' : 'Riattiva'}
                                            </button>
                                            {u.totp_stato && (
                                                <button className="gu-link gu-link-rosso"
                                                        onClick={() => { setAzzeraPer(u); setErrore(null); }}>
                                                    Azzera 2 passaggi
                                                </button>
                                            )}
                                        </div>
                                    ) : (
                                        <span className="gu-solo-admin">solo amministratore</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>
                )
            )}

            {/* --- Form nuovo utente --- */}
            {mostraForm && (
                <div className="gu-overlay" onClick={() => !salvataggio && chiudiForm()}>
                    <div className="gu-modale" onClick={(e) => e.stopPropagation()}>
                        <h2>Nuovo utente</h2>

                        <div className="gu-griglia">
                            <div className="gu-campo">
                                <label>Nome *</label>
                                <input value={form.nome} onChange={(e) => aggiorna('nome', e.target.value)} />
                            </div>
                            <div className="gu-campo">
                                <label>Cognome *</label>
                                <input value={form.cognome} onChange={(e) => aggiorna('cognome', e.target.value)} />
                            </div>
                            <div className="gu-campo">
                                <label>Username *</label>
                                <input value={form.username} autoComplete="off"
                                    onChange={(e) => aggiorna('username', e.target.value.trim())} />
                            </div>
                            <div className="gu-campo">
                                <label>Email *</label>
                                <input type="email" value={form.email} autoComplete="off"
                                    onChange={(e) => aggiorna('email', e.target.value.trim())} />
                            </div>
                            <div className="gu-campo">
                                <label>Telefono *</label>
                                <input type="tel" value={form.telefono} autoComplete="off"
                                    onChange={(e) => aggiorna('telefono', e.target.value)} />
                            </div>
                            <div className="gu-campo">
                                <label>Ruolo *</label>
                                <select value={form.ruolo} onChange={(e) => aggiorna('ruolo', e.target.value)}>
                                    {ruoliCreabili.map((r) => <option key={r.valore} value={r.valore}>{r.etichetta}</option>)}
                                </select>
                            </div>
                            {RUOLI_CLINICI.includes(form.ruolo) && (
                                <div className="gu-campo gu-campo-largo">
                                    <label>Medico collegato</label>
                                    <select value={form.medico_id} onChange={(e) => aggiorna('medico_id', e.target.value)}>
                                        <option value="">Nessuno (da collegare in seguito)</option>
                                        {mediciDisponibili.map((m) => (
                                            <option key={m.id} value={m.id}>{m.cognome} {m.nome}</option>
                                        ))}
                                    </select>
                                    <small className="gu-nota">
                                        Il collegamento al medico determina quali procedure e consensi l'utente vede.
                                    </small>
                                </div>
                            )}
                        </div>

                        <p className="gu-nota gu-nota-password">
                            La password provvisoria la genera il sistema e la manda
                            <strong> solo all&apos;indirizzo dell&apos;utente</strong>: non compare
                            qui e nessun altro la vede. Al primo accesso dovrà sostituirla.
                        </p>

                        {errore && <div className="gu-errore">{errore}</div>}

                        <div className="gu-modale-azioni">
                            <button className="gu-btn-annulla" onClick={chiudiForm} disabled={salvataggio}>
                                Annulla
                            </button>
                            <button className="gu-btn-primario" onClick={salva} disabled={salvataggio}>
                                {salvataggio ? 'Creazione…' : 'Crea utente'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- Reset password --- */}
            {resetPer && (
                <div className="gu-overlay" onClick={() => setResetPer(null)}>
                    <div className="gu-modale gu-modale-piccola" onClick={(e) => e.stopPropagation()}>
                        <h2>Reimposta password</h2>
                        <p className="gu-nota">
                            Nuova password per <strong>{resetPer.cognome} {resetPer.nome}</strong> ({resetPer.username}).
                            Comunicagliela tu: non viene inviata per email. Al primo accesso dovrà sostituirla.
                        </p>
                        <div className="gu-campo">
                            <label>Nuova password *</label>
                            <input type="text" value={nuovaPassword} autoComplete="new-password"
                                placeholder="min. 8 caratteri"
                                onChange={(e) => setNuovaPassword(e.target.value)} />
                        </div>
                        {errore && <div className="gu-errore">{errore}</div>}
                        <div className="gu-modale-azioni">
                            <button className="gu-btn-annulla" onClick={() => setResetPer(null)}>Annulla</button>
                            <button className="gu-btn-primario" onClick={confermaReset}>Aggiorna password</button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- Azzeramento del secondo fattore --- */}
            {azzeraPer && (
                <div className="gu-overlay" onClick={() => !azzerando && setAzzeraPer(null)}>
                    <div className="gu-modale gu-modale-piccola" onClick={(e) => e.stopPropagation()}>
                        <h2>Azzerare la verifica in due passaggi?</h2>
                        <p className="gu-nota">
                            Riguarda <strong>{azzeraPer.cognome} {azzeraPer.nome}</strong> ({azzeraPer.username}).
                        </p>
                        <div className="gu-esito gu-esito-ko">
                            Da subito rientra con la <strong>sola password</strong>, e i suoi
                            codici di recupero smettono di valere. Dovrà rifare
                            l&apos;attivazione dal proprio profilo.
                            <br /><br />
                            L&apos;operazione lascia un <strong>avviso ad alta priorità</strong> con
                            il tuo nome, e una notifica alla persona interessata: se non
                            è stata lei a chiederlo, lo scopre subito.
                            <br /><br />
                            Fallo solo dopo aver parlato con lei e aver riconosciuto la
                            voce: una richiesta arrivata per messaggio è esattamente la
                            forma che ha chi sta cercando di entrare al posto suo.
                        </div>
                        {errore && <div className="gu-errore">{errore}</div>}
                        <div className="gu-modale-azioni">
                            <button className="gu-btn-annulla" disabled={azzerando}
                                    onClick={() => setAzzeraPer(null)}>Annulla</button>
                            <button className="gu-btn-primario" disabled={azzerando}
                                    onClick={confermaAzzera}>
                                {azzerando ? 'Azzero…' : 'Azzera'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {reinviaPer && (
                <div className="gu-overlay" onClick={() => !reinviando && setReinviaPer(null)}>
                    <div className="gu-modale gu-modale-piccola" onClick={(e) => e.stopPropagation()}>
                        <h2>Inviare di nuovo le credenziali?</h2>
                        <p className="gu-nota">
                            A <strong>{reinviaPer.cognome} {reinviaPer.nome}</strong> ({reinviaPer.username}),
                            all&apos;indirizzo <strong>{reinviaPer.email}</strong>.
                        </p>
                        <div className="gu-esito gu-esito-ko">
                            Viene generata una <strong>password provvisoria nuova</strong> e
                            spedita per email: quella di adesso <strong>smette di valere</strong>.
                            Al primo accesso dovrà sostituirla con una sua.
                            <br /><br />
                            La password non compare qui: l&apos;email è l&apos;unico canale.
                            Se l&apos;email non parte, non cambia niente e te lo dico.
                        </div>
                        {errore && <div className="gu-errore">{errore}</div>}
                        <div className="gu-modale-azioni">
                            <button className="gu-btn-annulla" disabled={reinviando}
                                    onClick={() => setReinviaPer(null)}>Annulla</button>
                            <button className="gu-btn-primario" disabled={reinviando}
                                    onClick={confermaReinvio}>
                                {reinviando ? 'Invio…' : 'Invia'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {esitoReinvio && (
                <div className="gu-overlay" onClick={() => setEsitoReinvio('')}>
                    <div className="gu-modale gu-modale-piccola" onClick={(e) => e.stopPropagation()}>
                        <h2>Credenziali inviate</h2>
                        <div className="gu-esito gu-esito-ok">{esitoReinvio}</div>
                        <div className="gu-modale-azioni">
                            <button className="gu-btn-primario" onClick={() => setEsitoReinvio('')}>Chiudi</button>
                        </div>
                    </div>
                </div>
            )}

            {esitoAzzera && (
                <div className="gu-overlay" onClick={() => setEsitoAzzera('')}>
                    <div className="gu-modale gu-modale-piccola" onClick={(e) => e.stopPropagation()}>
                        <h2>Fatto</h2>
                        <p className="gu-nota">{esitoAzzera}</p>
                        <div className="gu-modale-azioni">
                            <button className="gu-btn-primario" onClick={() => setEsitoAzzera('')}>Chiudi</button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- Credenziali generate --- */}
            {credenziali && (
                <div className="gu-overlay" onClick={() => setCredenziali(null)}>
                    <div className="gu-modale gu-modale-piccola" onClick={(e) => e.stopPropagation()}>
                        <h2>Utente creato</h2>

                        {credenziali.emailInviata ? (
                            <div className="gu-esito gu-esito-ok">
                                ✓ Credenziali inviate a <strong>{credenziali.emailDestinatario}</strong>.
                                La password è arrivata solo lì.
                            </div>
                        ) : (
                            <div className="gu-esito gu-esito-ko">
                                L&apos;email a <strong>{credenziali.emailDestinatario}</strong> non è
                                partita. L&apos;account è comunque attivo: la collega entra da
                                <strong> «Password dimenticata»</strong> nella schermata di accesso,
                                che le manda il link al suo indirizzo. Se l&apos;indirizzo è
                                sbagliato, serve un amministratore.
                            </div>
                        )}

                        <p className="gu-nota">
                            Per <strong>{credenziali.nome}</strong>. Comunicale il nome utente:
                            la password non la vedi e non ti serve.
                        </p>
                        <div className="gu-credenziali">
                            <div><span>Username</span><code>{credenziali.username}</code></div>
                        </div>
                        <div className="gu-modale-azioni">
                            <button className="gu-btn-primario" onClick={() => setCredenziali(null)}>
                                Ho annotato le credenziali
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GestioneUtenti;
